import { useCallback, useEffect, useState, useSyncExternalStore, type FC } from 'react';
import { useFeatureVersion } from '@hooks/useFeatureVersion';
import { MENU_FEATURE_KEY } from '@utils/featureRollout/constants';
import {
  isPreviewActive,
  subscribePreview,
  stopPreview,
  hasOptedOutUsageFeedback,
  optOutUsageFeedback,
  markUsageFeedbackShown,
  wasUsageFeedbackShown,
} from '@utils/featureRollout/preview';
import { subscribeCoreActionCount, getCoreActionCount, type CoreActionType } from '@utils/featureRollout/usageSignals';
import { PreviewBar } from './PreviewBar';
import { RevertFeedbackPrompt } from './RevertFeedbackPrompt';
import { USAGE_FEEDBACK } from '@utils/featureRollout/strings';
import type { RevertFeedbackReason } from '@utils/featureRollout/strings';

// Capa de orquestación del PREVIEW per-usuario + feedback por USO (Items E#2 y E#3). Se monta
// en el shell del menú. Responsabilidades:
//   - Mostrar la barra de vista previa mientras isPreviewActive (no-root "echa un vistazo").
//   - Disparar el prompt de feedback OMITIBLE tras N core-actions reales (editar/reordenar/crear)
//     en la versión nueva — por USO, NO por tiempo. Cap per-usuario + opt-out durable respetado.
//
// NO escribe el cache de variantes ni el outbox de switch: el feedback va por el canal telemetría
// (submitUsageFeedback → behavior 'usage_feedback' con by_user_id).

// Umbral de core-actions que dispara el prompt. Elegido para que sea "uso real" (varias ediciones)
// sin esperar demasiado. Cap: una sola vez por sesión de uso (markUsageFeedbackShown).
export const USAGE_FEEDBACK_THRESHOLD = 4;

// Hook local: ¿el usuario está en vista previa? Suscribe al singleton per-tab.
function usePreviewActive(featureKey: string): boolean {
  return useSyncExternalStore(
    (cb) => subscribePreview(featureKey, cb),
    () => isPreviewActive(featureKey),
    () => isPreviewActive(featureKey),
  );
}

const MenuPreviewLayer: FC = () => {
  const { variant, submitUsageFeedback } = useFeatureVersion(MENU_FEATURE_KEY);
  const previewActive = usePreviewActive(MENU_FEATURE_KEY);
  const [showFeedback, setShowFeedback] = useState(false);

  // El feedback por uso aplica cuando el operador está VIENDO la versión nueva: en preview
  // (no-root) o ya adoptada (treatment). En control sin preview no hay nada que opinar.
  const onNewVersion = previewActive || variant === 'treatment';

  // Dispara el prompt tras N core-actions. Suscribe al contador per-usuario; respeta cap + opt-out.
  useEffect(() => {
    if (!onNewVersion) return undefined;
    const maybeShow = (count: number) => {
      if (count < USAGE_FEEDBACK_THRESHOLD) return;
      if (wasUsageFeedbackShown(MENU_FEATURE_KEY)) return;
      if (hasOptedOutUsageFeedback(MENU_FEATURE_KEY)) return;
      markUsageFeedbackShown(MENU_FEATURE_KEY);
      setShowFeedback(true);
    };
    // Evaluar el conteo ya acumulado (p.ej. el usuario editó antes de montar esta capa).
    maybeShow(getCoreActionCount());
    return subscribeCoreActionCount(maybeShow);
  }, [onNewVersion]);

  const onSubmit = useCallback(
    (reason: string, text: string) => {
      submitUsageFeedback(reason as RevertFeedbackReason, text, getCoreActionCount(), 'usage_feedback');
    },
    [submitUsageFeedback],
  );

  const onOptOut = useCallback(() => {
    optOutUsageFeedback(MENU_FEATURE_KEY);
    setShowFeedback(false);
  }, []);

  return (
    <>
      {previewActive && (
        <div style={{ marginBottom: 12 }}>
          <PreviewBar onExit={() => stopPreview(MENU_FEATURE_KEY)} />
        </div>
      )}
      {showFeedback && (
        <div style={{ marginBottom: 12 }}>
          <RevertFeedbackPrompt
            title={USAGE_FEEDBACK.title}
            subtitle={USAGE_FEEDBACK.subtitle}
            onSubmit={onSubmit}
            onSkip={() => setShowFeedback(false)}
            onOptOut={onOptOut}
            optOutLabel={USAGE_FEEDBACK.optOut}
          />
        </div>
      )}
    </>
  );
};

// Tipo re-exportado por conveniencia para los consumidores que registran core-actions.
export type { CoreActionType };

export default MenuPreviewLayer;
